/**
 * The « Configuration » tab of a branch page (plan « branches protégées »,
 * step 6; mockup « D — onglet Configuration d'une branche protégée »): the
 * client of `GET /projects/:project/environments/:environment/configuration`
 * of `waadoo-projects` (backend `plugins/waadooProjects/configurationRoute.ts`),
 * and the launch of the template `env-configure` (platform-catalog fork),
 * run by the backend action `waadoo:env:configure`.
 *
 * THE CHECKS ARE THE SERVER'S (step 8, part 2a, 2026-10-21): the route
 * sends the project's `AppType` descriptor as written (`descriptor`), and
 * the draft is planned with the same engine as `waadoo:env:configure`
 * (`@internal/plugin-waadoo-config-common`: `planWithDescriptor`, the locks,
 * the profiles, the peak) — the bounds of the caller's tier, the
 * descriptor's rules, volumes that only grow —, so the tab says why before
 * the task does; the server decides. The LAYOUT is the type's: Odoo's tab
 * is the Odoo module's contribution (genericity plan, step 4:
 * `plugins/odoo`, `odooConfiguration/`), the shared pieces are in
 * `configurationForm/`. The route's types are re-declared, its contract
 * changing on both sides at once.
 *
 * FAIL SOFT: the route exists only where `waadooProjects.gitlab.*` is set
 * (ludo); a 404 reads as « not offered here ». The live quota (`quota`) and
 * the reference configuration (« Comparer avec ») are reading aids: absent,
 * malformed or failed, they are simply not shown.
 */

import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import type { ScaffolderApi } from '@backstage/plugin-scaffolder-react';
import {
  compileDescriptor,
  type AppTypeDescriptor,
  type AppTypeSource,
  type ConfigurationBounds,
  type FieldValues,
} from '@internal/plugin-waadoo-config-common';
import {
  branchKind,
  branchTier,
  compareBranches,
  isEphemeralBranch,
  PRODUCTION_BRANCH,
} from './branchKind';

/** The backend plugin serving the route. */
const PLUGIN_ID = 'waadoo-projects';

export type ConfigurationLock =
  | 'read-only'
  | 'production'
  | 'ephemeral'
  | 'unreadable';

/**
 * Mirrored from the backend's `EnvironmentQuotaReading`: the live
 * ResourceQuota of the environment's namespace, its quantities as the API
 * server writes them (`2`, `1500m`, `3Gi`…).
 */
export interface EnvironmentQuotaReading {
  hard: Record<string, string>;
  used: Record<string, string>;
}

/**
 * Mirrored from the backend's `ConfigurationResponse`. `C`: the shape the
 * type's tab reads the configuration with — the backend sends the values of
 * the descriptor's configurable fields (`FieldValues`), which the tab of a
 * type whose descriptor it knows (Odoo's) reads as its own shape.
 */
export interface ConfigurationResponse<C extends FieldValues = FieldValues> {
  environment: string;
  kind: 'permanent' | 'ephemeral';
  active: boolean;
  configuration?: C;
  /**
   * The non-configurable fields (Mailpit's switch, Wagtail's `mediaS3`), read
   * tolerantly, an ephemeral branch's under the light version: what the
   * `when` of a field or a feature reads besides the configuration
   * (`environmentValues`). An answer without them (a backend older than
   * 2026-10-25) reads as none: every `when` that needs them is then
   * unevaluable, its field shown, its feature on.
   */
  inputs: FieldValues;
  problems: string[];
  access: 'admin' | 'editor' | 'read';
  editable: boolean;
  lock?: ConfigurationLock;
  bounds: { editor: ConfigurationBounds; admin: ConfigurationBounds };
  /** The project's `AppType` as written: the tab plans with it (header). */
  descriptor: AppTypeSource;
  appenvPath: string;
  files: string[];
  /** Absent without a live quota (asleep), or when it could not be read. */
  quota?: EnvironmentQuotaReading;
}

interface Apis {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The answer's shape; `configuration` is not checked against `C` — the
 * backend reads it with the same descriptor (`descriptor`).
 */
function isAnswer<C extends FieldValues>(
  value: unknown,
): value is ConfigurationResponse<C> {
  return (
    isRecord(value) &&
    typeof value.environment === 'string' &&
    (value.kind === 'permanent' || value.kind === 'ephemeral') &&
    typeof value.editable === 'boolean' &&
    Array.isArray(value.problems) &&
    isRecord(value.bounds) &&
    isRecord(value.bounds.editor) &&
    isRecord(value.bounds.admin) &&
    isRecord(value.descriptor) &&
    typeof value.appenvPath === 'string'
  );
}

/**
 * The descriptors compiled, by their JSON, the most recent last. Bounded: a
 * tab reads its project's descriptor and, for « Comparer avec », the same
 * one again; a catalog edit makes a new one at each refresh, and an old one
 * is never read again.
 */
const compiled = new Map<string, AppTypeDescriptor>();
const COMPILED_KEPT = 4;

/**
 * `source` compiled by the engine, once per descriptor; throws, as the
 * backend's compilation does, on a descriptor the engine cannot use — which
 * the backend, which compiled it first, never sends.
 */
export function descriptorOf(source: AppTypeSource): AppTypeDescriptor {
  const key = JSON.stringify(source);
  const descriptor = compiled.get(key) ?? compileDescriptor(source);
  // Moved last: the oldest is the first dropped.
  compiled.delete(key);
  compiled.set(key, descriptor);
  for (const oldest of compiled.keys()) {
    if (compiled.size <= COMPILED_KEPT) break;
    compiled.delete(oldest);
  }
  return descriptor;
}

const isStringRecord = (value: unknown): value is Record<string, string> =>
  isRecord(value) &&
  Object.values(value).every(entry => typeof entry === 'string');

/** The `quota` of an answer, or undefined when absent or malformed. */
function quotaOf(value: unknown): EnvironmentQuotaReading | undefined {
  return isRecord(value) &&
    isStringRecord(value.hard) &&
    isStringRecord(value.used)
    ? { hard: value.hard, used: value.used }
    : undefined;
}

/**
 * The answer; `undefined` on a 404 (not offered); rejects otherwise, and on
 * a descriptor the engine cannot compile (`descriptorOf`). A malformed
 * `quota` is dropped, not a failure: the gauges are optional.
 */
export async function fetchConfiguration<C extends FieldValues = FieldValues>(
  apis: Apis & {
    project: string;
    environment: string;
    /** Aborts the request (a reference given up); rejects with an AbortError. */
    signal?: AbortSignal;
  },
): Promise<ConfigurationResponse<C> | undefined> {
  // Absolute backend URL: in development the frontend runs on another port.
  const base = await apis.discoveryApi.getBaseUrl(PLUGIN_ID);
  const url = `${base}/projects/${encodeURIComponent(
    apis.project,
  )}/environments/${encodeURIComponent(apis.environment)}/configuration`;
  const response = await (apis.signal
    ? apis.fetchApi.fetch(url, { signal: apis.signal })
    : apis.fetchApi.fetch(url));
  if (response.status === 404) {
    return undefined;
  }
  if (!response.ok) {
    throw new Error(`Configuration API: HTTP ${response.status}`);
  }
  const body: unknown = await response.json();
  if (!isAnswer<C>(body)) {
    throw new Error('Configuration API: unexpected answer');
  }
  descriptorOf(body.descriptor);
  const { quota, inputs, ...rest } = body;
  // Tolerated absent (the field's comment); a malformed one, as absent.
  const answer = { ...rest, inputs: isRecord(inputs) ? inputs : {} };
  const reading = quotaOf(quota);
  return reading ? { ...answer, quota: reading } : answer;
}

/**
 * Every field value of the environment the answer reads — its
 * non-configurable fields, then its configuration —, what the `when` of a
 * descriptor's field or feature is evaluated over.
 */
export function environmentValues(
  answer: Pick<ConfigurationResponse, 'inputs' | 'configuration'>,
): FieldValues {
  return { ...answer.inputs, ...answer.configuration };
}

export type ConfigurationState<C extends FieldValues = FieldValues> =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'failed' }
  | { status: 'ready'; answer: ConfigurationResponse<C> };

/**
 * The configuration of `project/environment`, read again on a change of
 * `reloadSignal` (the page's Refresh). The last answer stays on screen while
 * a new one is read.
 */
export function useEnvironmentConfiguration<
  C extends FieldValues = FieldValues,
>(
  project: string,
  environment: string,
  reloadSignal?: number,
): ConfigurationState<C> {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const key = `${project}/${environment}`;
  const [state, setState] = useState<{
    key: string;
    value: ConfigurationState<C>;
  }>();

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const answer = await fetchConfiguration<C>({
          fetchApi,
          discoveryApi,
          project,
          environment,
        });
        if (active) {
          setState({
            key,
            value: answer
              ? { status: 'ready', answer }
              : { status: 'unavailable' },
          });
        }
      } catch {
        if (active) setState({ key, value: { status: 'failed' } });
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [fetchApi, discoveryApi, project, environment, key, reloadSignal]);

  return state && state.key === key ? state.value : { status: 'loading' };
}

/**
 * The environment name the « modèle dev-* » reference is read with: the
 * route answers every `dev-…` name with the same effective configuration —
 * dev's values under `ephemeral-template/values.yaml` — without asking git
 * whether the branch exists (backend `configurationRoute.ts`, header).
 */
export const EPHEMERAL_TEMPLATE_ENVIRONMENT = 'dev-template';

/** What « Comparer avec » may be set to. */
export interface ConfigurationReference {
  /** The environment the route is asked for. */
  environment: string;
  /** What the tab shows: the branch, or `dev-*` for the template. */
  label: string;
}

/** The template's reference, as offered next to the permanent branches. */
export const TEMPLATE_REFERENCE: ConfigurationReference = {
  environment: EPHEMERAL_TEMPLATE_ENVIRONMENT,
  label: 'dev-*',
};

/**
 * The references « Comparer avec » offers on `current`'s tab: the other
 * permanent branches in the order of every list (`compareBranches`), then
 * the ephemeral branches' template — but on an ephemeral branch, whose
 * configuration IS the template's. « aucun » is the tab's own option.
 */
export function referenceOptions(
  current: string,
  permanent: readonly string[],
): ConfigurationReference[] {
  const branches = permanent
    .filter(name => name !== current && !isEphemeralBranch(name))
    .sort(compareBranches)
    .map(name => ({ environment: name, label: name }));
  return isEphemeralBranch(current)
    ? branches
    : [...branches, TEMPLATE_REFERENCE];
}

/**
 * The reference shown by default: the tier just above `current`'s — prod
 * for a staging branch; the first staging branch (else prod) for dev and an
 * ephemeral branch; none for prod, nor when the branch above is not
 * listed. `permanent`: the project's permanent branches.
 */
export function defaultReference(
  current: string,
  permanent: readonly string[],
): string | undefined {
  const others = permanent
    .filter(name => name !== current && !isEphemeralBranch(name))
    .sort(compareBranches);
  const production = others.includes(PRODUCTION_BRANCH)
    ? PRODUCTION_BRANCH
    : undefined;
  switch (branchTier(current)) {
    case 'production':
      return undefined;
    case 'staging':
      return production;
    default:
      return others.find(name => branchTier(name) === 'staging') ?? production;
  }
}

/**
 * The configuration of the reference `environment`, read with the route of
 * the tab; undefined while asked, without a reference, and on any failure or
 * unreadable answer — a reference that cannot be read is not shown, it is
 * no error of the tab's.
 */
export function useReferenceConfiguration<C extends FieldValues = FieldValues>(
  project: string,
  environment: string | undefined,
  reloadSignal?: number,
): C | undefined {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const key = environment ? `${project}/${environment}` : undefined;
  const [state, setState] = useState<{
    key: string;
    configuration?: C;
  }>();

  useEffect(() => {
    if (!environment || !key) return undefined;
    // The request of a reference given up (another one picked, the tab
    // closed) is aborted, not merely ignored.
    const controller = new AbortController();
    const load = async () => {
      try {
        const answer = await fetchConfiguration<C>({
          fetchApi,
          discoveryApi,
          project,
          environment,
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setState({ key, configuration: answer?.configuration });
        }
      } catch {
        // An AbortError is ours: silent, and the state is left alone. Any
        // other failure reads as « no reference », never as an error.
        if (!controller.signal.aborted) setState({ key });
      }
    };
    void load();
    return () => {
      controller.abort();
    };
  }, [fetchApi, discoveryApi, project, environment, key, reloadSignal]);

  return state && state.key === key ? state.configuration : undefined;
}

const SUFFIX_FACTORS: Readonly<Record<string, number>> = {
  n: 1e-9,
  u: 1e-6,
  m: 1e-3,
  '': 1,
  k: 1e3,
  M: 1e6,
  G: 1e9,
  T: 1e12,
  P: 1e15,
  E: 1e18,
  Ki: 1024,
  Mi: 1024 ** 2,
  Gi: 1024 ** 3,
  Ti: 1024 ** 4,
  Pi: 1024 ** 5,
  Ei: 1024 ** 6,
};

const QUANTITY_PATTERN =
  /^([+-]?(?:\d+(?:\.\d*)?|\.\d+))(?:[eE]([+-]?\d+)|(Ki|Mi|Gi|Ti|Pi|Ei|n|u|m|k|M|G|T|P|E))?$/;

/**
 * A Kubernetes quantity (`250m`, `1.5Gi`, `2`, `1e3`) in base units — cores
 * for CPU, bytes for memory, a plain count for pods —, or undefined for a
 * string the API server would not have written. The backend's
 * `parseQuantity` (`plugins/waadooPlatform/capacity.ts`) restated: the
 * frontend cannot import it.
 */
export function parseQuantity(quantity: string): number | undefined {
  const match = QUANTITY_PATTERN.exec(quantity.trim());
  if (!match) return undefined;
  const [, number, exponent, suffix] = match;
  const value = Number(number);
  return exponent !== undefined
    ? value * 10 ** Number(exponent)
    : value * SUFFIX_FACTORS[suffix ?? ''];
}

/** One gauge of the tab: used against hard, in millicores, Mi or pods. */
export interface QuotaGauge {
  resource: 'cpu' | 'memory' | 'pods';
  used: number;
  hard: number;
}

const QUOTA_KEYS = {
  cpu: ['requests.cpu', 1000],
  memory: ['requests.memory', 1 / 1024 ** 2],
  pods: ['pods', 1],
} as const;

/**
 * The gauges of a live quota — the reserved CPU, the reserved memory, the
 * pods —, each only when both its hard and its used figure are readable
 * (`status.used` is empty until the quota controller has counted): never a
 * bar on an invented figure. Empty without a quota.
 */
export function quotaGauges(
  quota: EnvironmentQuotaReading | undefined,
): QuotaGauge[] {
  if (!quota) return [];
  return (Object.keys(QUOTA_KEYS) as Array<keyof typeof QUOTA_KEYS>).flatMap(
    resource => {
      const [key, scale] = QUOTA_KEYS[resource];
      const hardText = quota.hard[key];
      const usedText = quota.used[key];
      const hard = hardText === undefined ? undefined : parseQuantity(hardText);
      const used = usedText === undefined ? undefined : parseQuantity(usedText);
      return hard === undefined || used === undefined || hard <= 0
        ? []
        : [{ resource, used: used * scale, hard: hard * scale }];
    },
  );
}

/** The profile card of a configuration matching no profile of the type. */
export const CUSTOM_PROFILE = 'personnalise';

/** Whether applying to `branch` asks for `<project>/<branch>` typed. */
export function configureNeedsConfirmation(branch: string): boolean {
  return branchKind(branch) === 'production';
}

/** Starts the change; resolves to the task's id. */
export async function launchConfigure(
  scaffolderApi: Pick<ScaffolderApi, 'scaffold'>,
  options: {
    /**
     * The template to run: the project's own for this role
     * (`useProjectTemplates`, components/shared/templateRoles.ts).
     */
    templateRef: string;
    project: string;
    branch: string;
    /** The fields changed, by id (`waadoo:env:configure`'s `fields`). */
    fields: Partial<FieldValues>;
    confirmation?: string;
  },
): Promise<string> {
  const { taskId } = await scaffolderApi.scaffold({
    templateRef: options.templateRef,
    values: {
      project: options.project,
      environment: options.branch,
      fields: options.fields,
      ...(options.confirmation !== undefined
        ? { confirmation: options.confirmation }
        : {}),
    },
  });
  return taskId;
}
